/* rk-lw.js — JS de la familia rk-lw-* (/products/leather-wallet, migrada de GemPages).
   Custom elements con namespace propio de la página: no colisiona con rk-alk.js (acrylic-lamp-kit)
   aunque ambas familias convivan en el mismo theme. Consolidar en un rk-pdp-core compartido
   queda para una sesión dedicada de librería.
   Cargado con <script defer> desde cada sección que lo necesita; todo con guarda
   customElements.get() porque el tag puede aparecer varias veces en la misma página. */

/* ── Add-to-cart por AJAX (/cart/add.js) + drawer de UpCart ────────────────────────────
   Regla §6 del SOP: UpCart detecta el add y abre solo; los eventos y el fallback a /cart
   cubren el resto. Sin JS el form postea a /cart/add (progressive enhancement). */
if (!customElements.get('rk-lw-atc')) {
  class RkLwAtc extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.btn = this.querySelector('button[type="submit"]');
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.#add();
      });
    }

    async #add() {
      const id = parseInt(this.form.querySelector('[name="id"]')?.value || '0', 10);
      const quantity = parseInt(this.form.querySelector('[name="quantity"]')?.value || '1', 10) || 1;
      if (!id) return;
      if (this.btn) this.btn.disabled = true;
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity }] })
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        await this.#afterAdd();
      } catch (error) {
        window.location.href = '/cart';
      } finally {
        if (this.btn) this.btn.disabled = false;
      }
    }

    async #afterAdd() {
      try {
        const cart = await (await fetch('/cart.js')).json();
        for (const el of document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"], [data-cart-count]')) {
          el.textContent = cart.item_count;
        }
      } catch (error) { /* UpCart refresca el contador igual */ }
      for (const ev of ['cart:refresh', 'upcart:refresh', 'upcart:open']) {
        document.dispatchEvent(new CustomEvent(ev, { bubbles: true }));
      }
      setTimeout(() => {
        if (!document.querySelector('[id*="upcart" i], [class*="upcart" i]')) {
          window.location.href = '/cart';
        }
      }, 1200);
    }
  }
  customElements.define('rk-lw-atc', RkLwAtc);
}

/* ── Galería del buybox ────────────────────────────────────────────────────────────────
   Track principal con scroll-snap + tira de miniaturas sincronizada. El original NO
   autoavanza (medido 27 s: índice constante en 0), así que acá tampoco hay autoplay.
   Las flechas se ocultan en los extremos, igual que el original. */
if (!customElements.get('rk-lw-gallery')) {
  class RkLwGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rk-track]');
      this.thumbTrack = this.querySelector('[data-rk-thumbs]');
      this.thumbs = [...this.querySelectorAll('[data-rk-thumb]')];
      this.navBtns = [...this.querySelectorAll('[data-rk-nav]')];

      for (const btn of this.navBtns) {
        btn.addEventListener('click', () => {
          this.#nav(btn.dataset.rkNav === 'next' ? 1 : -1, btn.dataset.rkFor);
        });
      }
      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.#goTo(i)));

      this.track?.addEventListener('scroll', () => {
        clearTimeout(this._d);
        this._d = setTimeout(() => this.#sync(), 90);
      }, { passive: true });
      this.thumbTrack?.addEventListener('scroll', () => {
        clearTimeout(this._dt);
        this._dt = setTimeout(() => this.#sync(), 90);
      }, { passive: true });

      /* el video del slide 3 solo arranca cuando es el slide visible */
      this.videos = [...this.querySelectorAll('video')];
      this.#sync();
    }

    #index() {
      return Math.round(this.track.scrollLeft / this.track.clientWidth);
    }

    #thumbStep() {
      const [a, b] = this.thumbs;
      if (a && b) return Math.abs(b.getBoundingClientRect().left - a.getBoundingClientRect().left);
      return a ? a.getBoundingClientRect().width + 5 : 72;
    }

    #revealThumb(i) {
      const th = this.thumbs[i];
      if (!th || !this.thumbTrack) return;
      this.thumbTrack.scrollTo({
        left: th.offsetLeft - (this.thumbTrack.clientWidth - th.offsetWidth) / 2,
        behavior: 'smooth'
      });
    }

    #goTo(i) {
      const target = Math.min(Math.max(i, 0), this.thumbs.length - 1);
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: 'smooth' });
      this.#revealThumb(target);
    }

    #nav(dir, target) {
      if (target === 'thumbs' && this.thumbTrack) {
        /* una miniatura por click, no la tira entera */
        this.thumbTrack.scrollBy({ left: dir * this.#thumbStep(), behavior: 'smooth' });
        return;
      }
      this.#goTo(this.#index() + dir);
    }

    #sync() {
      const i = this.#index();
      this.thumbs.forEach((thumb, j) => thumb.classList.toggle('is-active', i === j));
      for (const video of this.videos) {
        const slide = video.closest('[data-rk-slide]');
        if (!slide) continue;
        if ([...this.track.children].indexOf(slide) === i) { video.play?.().catch(() => {}); }
        else { video.pause?.(); }
      }
      for (const btn of this.navBtns) {
        let hidden;
        if (btn.dataset.rkFor === 'thumbs' && this.thumbTrack) {
          const atStart = this.thumbTrack.scrollLeft < 4;
          const atEnd = this.thumbTrack.scrollLeft > this.thumbTrack.scrollWidth - this.thumbTrack.clientWidth - 4;
          hidden = btn.dataset.rkNav === 'next' ? atEnd : atStart;
        } else {
          hidden = btn.dataset.rkNav === 'next' ? i >= this.thumbs.length - 1 : i <= 0;
        }
        btn.classList.toggle('is-hidden', hidden);
      }
    }
  }
  customElements.define('rk-lw-gallery', RkLwGallery);
}

/* ── Selector de color (variantes) ─────────────────────────────────────────────────────
   Propaga la variante elegida a TODOS los forms de add-to-cart de la página (buybox y
   sticky), actualiza precio/compare-at si difieren y refleja el ?variant= en la URL. */
if (!customElements.get('rk-lw-swatches')) {
  class RkLwSwatches extends HTMLElement {
    connectedCallback() {
      this.inputs = [...this.querySelectorAll('input[type="radio"]')];
      for (const input of this.inputs) {
        input.addEventListener('change', () => this.#select(input));
      }
    }

    #select(input) {
      const id = input.value;
      if (!id) return;
      for (const field of document.querySelectorAll('form [name="id"]')) field.value = id;
      const price = input.dataset.price;
      const compare = input.dataset.compare;
      for (const el of document.querySelectorAll('[data-rk-price]')) if (price) el.innerHTML = price;
      for (const el of document.querySelectorAll('[data-rk-compare]')) if (compare) el.innerHTML = compare;
      try {
        const url = new URL(window.location);
        url.searchParams.set('variant', id);
        window.history.replaceState({}, '', url);
      } catch (error) { /* en webviews puede fallar; no es crítico */ }
    }
  }
  customElements.define('rk-lw-swatches', RkLwSwatches);
}

/* ── Acordeón ──────────────────────────────────────────────────────────────────────────
   <details> nativo + animación de apertura/cierre. El original arranca TODO cerrado
   (medido: overflow hidden, height 0) tanto en el buybox (4 ítems) como en el FAQ (9). */
if (!customElements.get('rk-lw-accordion')) {
  class RkLwAccordion extends HTMLElement {
    connectedCallback() {
      for (const details of this.querySelectorAll('details')) {
        const summary = details.querySelector('summary');
        const panel = details.querySelector('[data-rk-panel]');
        if (!summary || !panel) continue;
        summary.addEventListener('click', (event) => {
          event.preventDefault();
          this.#toggle(details, panel);
        });
      }
    }

    #toggle(details, panel) {
      if (details.dataset.animating) return;
      details.dataset.animating = '1';
      const done = () => delete details.dataset.animating;
      if (details.open) {
        const anim = panel.animate(
          [{ blockSize: `${panel.scrollHeight}px` }, { blockSize: '0px' }],
          { duration: 250, easing: 'ease' }
        );
        anim.onfinish = () => { details.open = false; done(); };
      } else {
        details.open = true;
        panel.animate(
          [{ blockSize: '0px' }, { blockSize: `${panel.scrollHeight}px` }],
          { duration: 250, easing: 'ease' }
        ).onfinish = done;
      }
    }
  }
  customElements.define('rk-lw-accordion', RkLwAccordion);
}

/* ── Mini-carrusel de las cards de "You May Also Like" ─────────────────────────────────
   Cada card del original tiene su propio slider de imágenes con flechas rectangulares. */
if (!customElements.get('rk-lw-cards')) {
  class RkLwCards extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rk-cardtrack]');
      for (const btn of this.querySelectorAll('[data-rk-cardnav]')) {
        btn.addEventListener('click', () => {
          const dir = btn.dataset.rkCardnav === 'next' ? 1 : -1;
          const w = this.track.clientWidth;
          const i = Math.round(this.track.scrollLeft / w) + dir;
          const max = this.track.children.length - 1;
          this.track.scrollTo({ left: Math.min(Math.max(i, 0), max) * w, behavior: 'smooth' });
        });
      }
    }
  }
  customElements.define('rk-lw-cards', RkLwCards);
}
